import { useState, useEffect } from "react";
import { useLoaderData, useNavigate } from "@remix-run/react";
import ApplyWithSeekIcon from "~/components/icons/apply_with_seek.svg";
import { ErrorFlash } from "~/components/flash";
import { SeekAuthTab } from "../utils/seek_auth_tab.client";
import type { SeekLoaderData } from "../utils/seek_loader.server";

export const ApplyWithSeekButton = () => {
  const navigate = useNavigate();
  const [isLoadingSeek, setIsLoadingSeek] = useState(false);

  const loaderData = useLoaderData<SeekLoaderData>();
  const seekData = loaderData?.seek;

  const applyWithSeekEnabled = seekData?.applyWithSeekEnabled ?? false;
  const isConfigured = seekData?.isConfigured ?? false;
  const isPreview = seekData?.isPreview ?? false;
  const applyWithSeek = seekData?.applyWithSeek;
  const seekAuthUrl = seekData?.seekAuthUrl;
  const seekProfile = seekData?.seekProfile;
  const errorMessage = seekData?.error;
  const hasInvalidSeekParams = seekData?.hasInvalidSeekParams ?? false;

  useEffect(() => {
    if (hasInvalidSeekParams) {
      const currentUrl = new URL(window.location.href);
      currentUrl.searchParams.delete("seek-token");
      currentUrl.searchParams.delete("seek-prefill-id");
      // Use navigate with replace to update URL without full page reload
      // This preserves form state without a full page reload
      navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
    }
  }, [hasInvalidSeekParams, navigate]);

  if (!seekData) {
    return null;
  }

  const handleApplyWithSeekClick = async () => {
    if (isPreview) {
      return;
    }

    if (seekProfile) {
      return; // User already authenticated, no action needed
    }

    if (!seekAuthUrl) {
      console.error("Seek auth URL is not available");
      return;
    }

    setIsLoadingSeek(true);
    try {
      const authTab = new SeekAuthTab();
      const result = await authTab.authenticate(seekAuthUrl);

      if (result && (result.seekToken || result.seekPrefillId)) {
        const currentUrl = new URL(window.location.href);
        if (result.seekToken) {
          currentUrl.searchParams.set("seek-token", result.seekToken);
        }
        if (result.seekPrefillId) {
          currentUrl.searchParams.set("seek-prefill-id", result.seekPrefillId);
        }

        // Use navigate with replace to update URL without full page reload
        // This preserves form state without a full page reload
        navigate(`${currentUrl.pathname}${currentUrl.search}`, { replace: true });
      }
    } finally {
      setIsLoadingSeek(false);
    }
  };

  // Render button if:
  // - In preview mode OR
  // - Seek is configured (based on the 3 conditions below)
  //   - Seek is enabled globally (via env var) AND
  //   - the job board is configured (has seekId) AND
  //   - the job post has apply_with_seek enabled
  if (isPreview || (applyWithSeekEnabled && isConfigured && applyWithSeek)) {
    return (
      <>
        {errorMessage && <ErrorFlash message={errorMessage} />}
        <button
          data-testid="apply-with-seek-button"
          onClick={() => handleApplyWithSeekClick()}
          disabled={isPreview || !!errorMessage || isLoadingSeek}
          aria-label="Apply with Seek"
          type="button"
        >
          <img src={ApplyWithSeekIcon} alt="Apply with Seek" />
        </button>
      </>
    );
  }

  return null;
};

export default ApplyWithSeekButton;
